import { of } from 'rxjs';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { FootballReference } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { navigateBackOrFallback, ScreenInitialLoading } from "../../shared/screen-shell/screen-navigation.utils";

@Component({
  selector: "app-player-football-references",
  templateUrl: "./player-football-references.component.html",
  styleUrls: ["../player.component.css"],
  standalone: false
})
export class PlayerFootballReferencesComponent implements OnInit, OnDestroy {

  public references: FootballReference[] = [];
  public loading = false;
  public errorMsg: string;
  public page = 0;
  public pageSize = 20;
  public hasMore = false;
  public total = 0;
  public searchQuery = "";
  public playerId: string;
  public playerName: string;
  public showInitialLoading = false;
  private contextEventId: string;
  private returnToPlayerDetail = false;
  private returnPlayerId: string;
  private returnEventId: string;
  private readonly initialLoading = new ScreenInitialLoading(300, (visible) => this.showInitialLoading = visible);

  private readonly search$ = new Subject<string>();
  private searchSubscription: Subscription;

  constructor(
    private playerService: PlayerService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.playerId = this.route.snapshot.params["playerId"];
    this.contextEventId = this.route.snapshot.queryParamMap.get("contextEventId");
    this.returnToPlayerDetail = this.route.snapshot.queryParamMap.get("returnToPlayerDetail") === "true";
    this.returnPlayerId = this.route.snapshot.queryParamMap.get("returnPlayerId");
    this.returnEventId = this.route.snapshot.queryParamMap.get("returnEventId");
    this.searchSubscription = this.search$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((query) => {
      this.searchQuery = query || "";
      this.loadPage(0);
    });
    this.loadPage(0);
  }

  public ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.initialLoading.destroy();
  }

  public goBack(): void {
    this.prepareReturnToPlayerDetail();
    navigateBackOrFallback(this.location, this.router, this.playerId ? ["/profile", this.playerId] : ["/event-list"]);
  }

  public onSearch(value: string): void {
    this.search$.next(value || "");
  }

  public clearSearch(): void {
    this.searchQuery = "";
    this.search$.next("");
  }

  public loadMore(): void {
    if (this.loading || !this.hasMore) {
      return;
    }
    this.loadPage(this.page + 1);
  }

  public isContentLoading(): boolean {
    return this.showInitialLoading && this.references.length === 0;
  }

  public openReferenceProfile(reference: FootballReference): void {
    if (!reference || !reference.playerId || !reference.openProfile) {
      return;
    }
    this.router.navigate(["/profile", reference.playerId]);
  }

  public displayName(reference: FootballReference): string {
    return reference ? (reference.nickName || reference.name || "-") : "-";
  }

  public profileTitle(reference: FootballReference): string {
    return reference && reference.openProfile
      ? this.translate.instant("players.openProfile")
      : this.translate.instant("players.referenceProfilePrivate");
  }

  public initials(reference: FootballReference): string {
    const name = this.displayName(reference);
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
  }

  public dateLabel(value: Date | string): string {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    const locale = this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  private initialPageConsumed = false;

  private loadPage(page: number): void {
    const prepared = !this.initialPageConsumed && this.route.snapshot.data['screen'];
    this.initialPageConsumed = true;
    if (!this.playerId) {
      return;
    }
    this.beginLoading(page);
    this.errorMsg = null;
    (prepared ? of(prepared) : this.playerService.getFootballReferences(this.playerId, this.searchQuery, page, this.pageSize, this.contextEventId)).subscribe(
      (response) => {
        this.page = response.page;
        this.total = response.total || 0;
        this.hasMore = response.hasMore;
        this.playerName = response.playerNickName || response.playerName || this.playerName;
        this.references = page === 0 ? (response.items || []) : this.references.concat(response.items || []);
        this.finishLoading();
      },
      () => {
        this.finishLoading();
        this.errorMsg = this.translate.instant("players.footballReferencesLoadFailed");
      }
    );
  }

  private prepareReturnToPlayerDetail(): void {
    if (!this.returnToPlayerDetail || !this.returnPlayerId) {
      return;
    }
    localStorage.setItem("openPlayerDetailForPlayerId", this.returnPlayerId);
    if (this.returnEventId) {
      localStorage.setItem("openPlayerDetailForEventId", this.returnEventId);
    }
  }

  private beginLoading(page: number): void {
    this.loading = true;
    if (page === 0) {
      this.hasMore = false;
    }
    this.initialLoading.begin(page === 0 && this.references.length === 0, () => this.loading);
  }

  private finishLoading(): void {
    this.loading = false;
    this.initialLoading.finish();
  }
}
